import { APIClient } from 'infra/APIClient'
import { APIConfig, CursorPaged } from 'types/definitions'
import { rethrowRequestError } from 'utils/error-util'
import {
  BillingMethodDTO,
  CreateBillingMethodPayload,
  CreditBillingMethod,
  EmailCheckoutLinkPayload,
  StripeCheckoutSession,
  SetupIntentDto,
  SetupIntentPayload,
} from './dtos/billing-method'
import {
  BillingProfileDTO,
  UpdateBillingProfilePayload,
} from './dtos/billing-profile'
import {
  AddUserCouponPayload,
  CouponDTO,
  CreateCouponPayload,
  FindCouponQuery,
  UpdateCouponPayload,
  UserCouponDTO,
} from './dtos/coupon'
import {
  CreatePayoutMethodPayload,
  PayoutMethodDTO,
} from './dtos/payout-method'
import {
  AdjustCreditPayload,
  BillingTransactionDTO,
  CalculatePayload,
  CalculationResult,
  DepositToCreditPayload,
  FetchBillingTransactionsPayload,
} from './dtos/billing-transaction'

export interface TaskResult {
  code: 'SUCCESS' | 'ERROR'
  message: string
  data?: Record<string, unknown>
}

export interface BillingAPI {
  billing: BillingProfileAPI
  calculate: CalculationAPI
  coupon: CouponAPI
  userCoupon: UserCouponAPI
  checkout: CheckoutAPI
}

export function createBillingAPI(opts: APIConfig): BillingAPI {
  return {
    billing: new BillingProfileAPI(opts),
    calculate: new CalculationAPI(opts),
    coupon: new CouponAPI(opts),
    userCoupon: new UserCouponAPI(opts),
    checkout: new CheckoutAPI(opts),
  }
}

export class BillingProfileAPI extends APIClient {
  getMemberBillingProfile(
    workspaceId: string,
    userPublicId: string,
  ): Promise<BillingProfileDTO> {
    return this.client
      .get(`/v3/workspaces/${workspaceId}/billing-profiles/${userPublicId}`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  getTeamBillingProfile(teamId: string): Promise<BillingProfileDTO> {
    return this.client
      .get(`/v3/teams/${teamId}/billing-profile`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  updateMemberBillingProfile(
    workspaceId: string,
    data: UpdateBillingProfilePayload,
    userPublicId: string,
  ): Promise<BillingProfileDTO | null> {
    return this.client
      .patch(
        `/v3/workspaces/${workspaceId}/billing-profiles/${userPublicId}`,
        data,
      )
      .then(this._data)
      .catch(rethrowRequestError)
  }

  updateTeamBillingProfile(
    teamId: string,
    data: UpdateBillingProfilePayload,
  ): Promise<BillingProfileDTO | null> {
    return this.client
      .patch(`/v3/teams/${teamId}/billing-profile`, data)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  addBillingMethod(
    data: CreateBillingMethodPayload,
  ): Promise<BillingMethodDTO> {
    return this.client
      .post('/v3/billing-methods', data)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  removeBillingMethod(billingMethodId: string): Promise<TaskResult> {
    return this.client
      .delete(`/v3/billing-methods/${billingMethodId}`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  addPayoutMethod(
    data: CreatePayoutMethodPayload,
  ): Promise<PayoutMethodDTO | null> {
    return this.client
      .post('/v3/payout-methods', data)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  removePayoutMethod(payoutMethodId: string): Promise<TaskResult> {
    return this.client
      .delete(`/v3/payout-methods/${payoutMethodId}`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  depositCredit(
    billingMethodId: string,
    data: DepositToCreditPayload,
  ): Promise<CreditBillingMethod> {
    return this.client
      .post(`/v3/billing-methods/${billingMethodId}/deposit`, data)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  adjustCredit(
    billingMethodId: string,
    data: AdjustCreditPayload,
  ): Promise<CreditBillingMethod> {
    return this.client
      .post(`/v3/billing-methods/${billingMethodId}/adjust`, data)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  getTransactions(billingMethodId: string) {
    return this.client
      .get(`/v3/billing-methods/${billingMethodId}/transactions`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  getTransactionsByIds(
    payload: FetchBillingTransactionsPayload,
  ): Promise<BillingTransactionDTO[]> {
    return this.client
      .post(`/v3/transactions/id-query`, payload)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  getPayoutMethod(userPublicId: string) {
    return this.client
      .get(`/v3/users/${userPublicId}/payout-method`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  async setupStripePaymentMethodIntent(
    setupIntentPayload: SetupIntentPayload,
  ): Promise<SetupIntentDto> {
    return this.client
      .post(`/v3/stripe-payment-methods/setup-intent`, setupIntentPayload)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  async completeSetupStripePaymentMethodIntent(
    setupIntentId: string,
  ): Promise<PayoutMethodDTO> {
    return this.client
      .post(`/v3/stripe-payment-methods/setup-intent/complete`, {
        setup_intent_id: setupIntentId,
      })
      .then(this._data)
      .catch(rethrowRequestError)
  }
}

export class CalculationAPI extends APIClient {
  calculate(
    params: CalculatePayload,
    userId?: string,
  ): Promise<CalculationResult> {
    return this.client
      .get(`/v3/users/${userId || 'me'}/calculate`, { params })
      .then(this._data)
      .catch(rethrowRequestError)
  }
}

export class CouponAPI extends APIClient {
  find({
    workspace_id: workspaceId,
    ...query
  }: FindCouponQuery): Promise<CursorPaged<CouponDTO>> {
    return this.client
      .get(`/v3/workspaces/${workspaceId}/coupons`, { params: query })
      .then(this._data)
      .catch(rethrowRequestError)
  }

  get(id: string): Promise<CouponDTO | null> {
    return this.client.get(`/v3/coupons/${id}`).then(this._data)
  }

  getCouponByCode(
    code: string,
    workspaceId: string,
  ): Promise<CouponDTO | null> {
    return this.client
      .get(`/v3/workspaces/${workspaceId}/coupons/${code}`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  create(data: CreateCouponPayload): Promise<CouponDTO> {
    return this.client
      .post('/v3/coupons', data)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  update(id: string, data: UpdateCouponPayload): Promise<CouponDTO> {
    return this.client
      .patch(`/v3/coupons/${id}`, data)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  remove(id: string): Promise<TaskResult> {
    return this.client
      .delete(`/v3/coupons/${id}`)
      .then(this._data)
      .catch(rethrowRequestError)
  }
}

export class UserCouponAPI extends APIClient {
  getCouponsForUser(
    workspaceId: string,
    userPublicId?: string,
  ): Promise<UserCouponDTO[]> {
    return this.client
      .get(`/v3/workspaces/${workspaceId}/users/${userPublicId}/coupons`)
      .then(this._data)
  }

  addCouponToUser(
    code: string,
    idParams: AddUserCouponPayload,
  ): Promise<UserCouponDTO> {
    return this.client
      .post(
        `/v3/workspaces/${idParams.workspace_id}/users/${idParams.user_public_id}/coupons/${code}`,
      )
      .then(this._data)
      .catch(rethrowRequestError)
  }
}

export class CheckoutAPI extends APIClient {
  async generateCheckoutLink(jobId: string): Promise<StripeCheckoutSession> {
    return this.client
      .post(`/v3/stripe/generate-checkout-link/${jobId}`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  async emailCheckoutLink(payload: EmailCheckoutLinkPayload): Promise<void> {
    return this.client
      .post(`/v3/stripe/email-checkout-link`, payload)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  async invalidateCheckoutSessions(jobId: string): Promise<void> {
    return this.client
      .post(`/v3/stripe/invalidate-checkout-sessions/${jobId}`)
      .then(this._data)
      .catch(rethrowRequestError)
  }
}
